import { CameraIcon, RotateCcwIcon, XIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

type Props = {
  open: boolean
  onOpenChange(open: boolean): void
  onCapture(files: File[]): void
}

type ErrorReason = 'denied' | 'no-device' | 'insecure' | 'unsupported' | 'unknown'

const classifyError = (err: unknown): ErrorReason => {
  if (!(err instanceof Error)) return 'unknown'
  if (err.name === 'NotAllowedError' || err.name === 'SecurityError') return 'denied'
  if (err.name === 'NotFoundError' || err.name === 'OverconstrainedError') return 'no-device'
  return 'unknown'
}

export const CameraCaptureModal = ({ open, onOpenChange, onCapture }: Props) => {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [shots, setShots] = useState<{ file: File; url: string }[]>([])
  const [error, setError] = useState<ErrorReason | null>(null)
  const [starting, setStarting] = useState(false)

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  const startStream = useCallback(async () => {
    setError(null)
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(window.isSecureContext === false ? 'insecure' : 'unsupported')
      return
    }
    setStarting(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
    } catch (err) {
      setError(classifyError(err))
    } finally {
      setStarting(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      startStream()
      setShots([])
    } else {
      stopStream()
      setShots(prev => {
        prev.forEach(s => URL.revokeObjectURL(s.url))
        return []
      })
      setError(null)
    }
    return () => {
      stopStream()
    }
  }, [open, startStream, stopStream])

  const captureFrame = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(
      blob => {
        if (!blob) return
        const file = new File([blob], `${Date.now()}.jpg`, { type: 'image/jpeg' })
        const url = URL.createObjectURL(blob)
        setShots(prev => [...prev, { file, url }])
      },
      'image/jpeg',
      0.9,
    )
  }

  const removeShot = (index: number) => {
    setShots(prev => {
      const next = [...prev]
      const removed = next.splice(index, 1)
      removed.forEach(s => URL.revokeObjectURL(s.url))
      return next
    })
  }

  const handleDone = () => {
    onCapture(shots.map(s => s.file))
    onOpenChange(false)
  }

  const handleFallbackFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (files.length === 0) return
    onCapture(files)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        size="wide"
        anchor="top"
        className="max-h-[90vh] flex flex-col"
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('cameraCapture.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-3">
          {error ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <p className="text-sm text-text-primary">{t(`errors.error_camera-${error}`)}</p>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                {t('cameraCapture.useFilePickerButton')}
              </Button>
              {error === 'denied' && (
                <Button variant="ghost" size="sm" onClick={startStream}>
                  <RotateCcwIcon className="size-3.5" />
                  {t('global.tryAgain')}
                </Button>
              )}
            </div>
          ) : (
            <div className="relative bg-black rounded-md overflow-hidden aspect-video flex items-center justify-center">
              <video ref={videoRef} autoPlay playsInline muted className="size-full object-cover" />
              {starting && (
                <p className="absolute text-white text-sm">{t('cameraCapture.starting')}</p>
              )}
            </div>
          )}

          {shots.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {shots.map((shot, i) => (
                <div
                  key={shot.url}
                  className="relative size-16 rounded-md overflow-hidden border border-strong-01"
                >
                  <img src={shot.url} alt="" className="size-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeShot(i)}
                    className="absolute top-0 right-0 bg-black/60 text-white rounded-bl-md p-0.5"
                  >
                    <XIcon className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={handleFallbackFiles}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('global.cancel')}
          </Button>
          <div className="flex-1" />
          {!error && (
            <Button
              type="button"
              onClick={captureFrame}
              disabled={starting || !streamRef.current}
              className={cn('gap-2')}
            >
              <CameraIcon className="size-4" />
              {t('cameraCapture.captureButton')}
            </Button>
          )}
          <Button onClick={handleDone} disabled={shots.length === 0}>
            {t('global.done')} ({shots.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
